import { DestroyRef, inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';
import { IFormFieldError, ToasterComponentData, ToasterWidgetComponent, ToastType } from './toaster-widget.component';
import { tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class ToasterService {
  private isSnackbarOpen: boolean = false;
  private notificationQueue: Array<() => void> = [];
  private snackBarRef!: MatSnackBarRef<ToasterWidgetComponent>;
  private destroyRef = inject(DestroyRef);
  private snackBar = inject(MatSnackBar);

  private static get config(): MatSnackBarConfig {
    return {
      duration: 6000,
      direction: 'ltr',
      horizontalPosition: 'center',
      verticalPosition: 'top',
    } as MatSnackBarConfig;
  }

  public success(title: string = '', className: string = '') {
    this.addToQueue(() => this.show(ToastType.SUCCESS, title, className));
  }

  public error(title: string = '', className: string = '') {
    this.addToQueue(() => this.show(ToastType.ERROR, title, className));
  }

  public warning(title: string = '', className: string = '') {
    this.addToQueue(() => this.show(ToastType.WARNING, title, className));
  }

  public info(title: string = '', className: string = '') {
    this.addToQueue(() => this.show(ToastType.INFO, title, className));
  }

  public close() {
    this.snackBar.dismiss();
    this.isSnackbarOpen = false;

    if (this.notificationQueue.length) {
      const nextNotification = this.notificationQueue.shift();
      if (nextNotification) {
        nextNotification();
      }
    }
  }

  public showWithContent(
    type: ToastType,
    data: {
      title?: string;
      message?: string;
      loggerCode?: string;
      errors?: IFormFieldError[];
    },
  ) {
    this.addToQueue(() => {
      this.snackBarRef = this.snackBar.openFromComponent(ToasterWidgetComponent, {
        ...ToasterService.config,
        data: {
          type,
          title: data.title,
          content: {
            message: data.message,
            loggerCode: data.loggerCode,
            errors: data.errors,
          },
        } as ToasterComponentData,
      });
    });
  }

  private show(type: ToastType, title: string, className: string = '') {
    this.isSnackbarOpen = true;

    this.snackBarRef = this.snackBar.openFromComponent(ToasterWidgetComponent, {
      ...ToasterService.config,
      panelClass: className,
      data: {
        type,
        title,
      } as ToasterComponentData,
    });

    this.snackBarRef
      .afterDismissed()
      .pipe(
        tap(() => this.close()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private addToQueue(notificationFn: () => void) {
    if (this.isSnackbarOpen) {
      this.notificationQueue.push(notificationFn);
    } else {
      notificationFn();
    }
  }
}
